import {
  Box,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as R from 'ramda';
import { useMemo, useState } from 'react';

import {
  Button,
  FeedbackRatings,
  FeedbackSuccess,
  LabelCheckbox,
  ModalContentContainer,
} from '@/components';
import { Modals } from '@/constants';
import { EVENT_CONSTANTS } from '@/services';
import { appStyles } from '@/theme';
import { Episode, EpisodeFeedback } from '@/types';

import { ShareEpisodeForm } from '../ShareEpisodeForm';
import CoverImage from './CoverImage';

export const ReasonOptions = {
  AUDIO_QUALITY: 'Audio Quality',
  EDUCATIONAL_VALUE: 'Educational Value',
  GUEST: 'Guest',
  ENTERTAINMENT_VALUE: 'Entertainment Value',
  HOST_QUALITY: 'Host Quality',
} as const;

const ratingReasonsInitialState = Object.fromEntries(
  Object.entries(ReasonOptions).map(([_, value]) => [value, false]),
);

interface EpisodeFeedbackModalContentProps {
  episode: Episode;
  submitFeedback: ({ feedback }: { feedback: EpisodeFeedback }) => void;
  isSubmitting: boolean;
  isFeedbackSubmitted: boolean;
  onClose: () => void;
}

type RatingState = [number, React.Dispatch<React.SetStateAction<number>>];
type RatingReasonsState = [
  typeof ratingReasonsInitialState,
  React.Dispatch<React.SetStateAction<typeof ratingReasonsInitialState>>,
];

export const EpisodeFeedbackModalContent = ({
  episode,
  submitFeedback,
  isSubmitting,
  isFeedbackSubmitted,
  onClose,
}: EpisodeFeedbackModalContentProps) => {
  const [textFeedback, setTextFeedback] = useState('');
  const [rating, setRating]: RatingState = useState(0);
  const [ratingReasons, setRatingReasons]: RatingReasonsState = useState(
    ratingReasonsInitialState,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const feedback: EpisodeFeedback = useMemo(() => {
    const reasons = R.pipe(
      R.filter(a => !!a),
      R.keys,
      R.map(reason => reason.toString()),
    )(ratingReasons);

    return {
      episodeRating: rating,
      ratingReason: reasons,
      textFeedback,
    };
  }, [rating, ratingReasons, textFeedback]);

  const isRatingSelected = rating > 0;
  const isRatingReasonSelected = Object.values(ratingReasons).some(
    reason => reason,
  );

  return isMobile ? (
    <Box>
      <ModalContentContainer onModalClose={onClose} stretch>
        <Box>
          {isFeedbackSubmitted ? (
            <FeedbackSuccess />
          ) : (
            <>
              <Typography variant="h2" mb={3}>
                Episode Feedback
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 3,
                }}>
                <Box
                  sx={{
                    width: 196,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                  <CoverImage src={episode?.cover} dimension={100} />
                  <Typography variant="body2SemiBold" textAlign="center">
                    {episode?.name}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Rating rating={rating} setRating={setRating} />
                {isRatingSelected && (
                  <RatingReasons
                    rating={rating}
                    ratingReasons={ratingReasons}
                    setRatingReasons={setRatingReasons}
                  />
                )}
                {isRatingReasonSelected && (
                  <TextFeedback
                    rating={rating}
                    textFeedback={textFeedback}
                    setTextFeedback={setTextFeedback}
                  />
                )}
              </Box>
            </>
          )}
          <Box sx={{ mt: 2 }}>
            {isFeedbackSubmitted ? (
              <Button
                variant="primary"
                label="Done"
                skinny={false}
                onClick={onClose}
              />
            ) : (
              <Button
                variant="primary"
                label="Submit"
                skinny={false}
                disabled={
                  isSubmitting || !isRatingSelected || !isRatingReasonSelected
                }
                onClick={() => submitFeedback({ feedback })}
              />
            )}
          </Box>
        </Box>
      </ModalContentContainer>
    </Box>
  ) : (
    <Box sx={{ width: 800, height: 800 }}>
      <ModalContentContainer
        onModalClose={onClose}
        stretch
        leftColumnContents={
          <Box sx={{ maxWidth: 200, mx: 'auto' }}>
            <CoverImage src={episode?.cover} dimension={200} />
            <Typography variant="body2SemiBold" sx={{ color: 'text.contrast' }}>
              {episode?.name}
            </Typography>
          </Box>
        }>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
          {isFeedbackSubmitted ? (
            rating >= 4 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  gap: 3,
                }}>
                <Box>
                  <Typography variant="h2" mb={1}>
                    Thanks for your feedback!
                  </Typography>
                  <Typography variant="body2">
                    We&apos;re glad you enjoyed this episode. Why not share it
                    with a colleague or friend so they can learn from it too?
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    gap: 1,
                  }}>
                  <Typography variant="h4">Share episode</Typography>
                  <ShareEpisodeForm
                    episodeId={episode.episodeId}
                    episodeDeeplink={episode.deepLink}
                    referrer={{
                      component: EVENT_CONSTANTS.COMPONENT.MODAL,
                      modalName: Modals.EPISODE_FEEDBACK,
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <FeedbackSuccess />
            )
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                flex: 1,
              }}>
              <Box>
                <Typography variant="h2" mb={1}>
                  Episode Feedback
                </Typography>
                <Typography variant="body2">
                  We truly appreciate your feedback! We read every word to help
                  us create a more valuable LumiQ experience.
                </Typography>
              </Box>
              <Rating rating={rating} setRating={setRating} />

              {isRatingSelected && (
                <RatingReasons
                  rating={rating}
                  ratingReasons={ratingReasons}
                  setRatingReasons={setRatingReasons}
                />
              )}

              {isRatingReasonSelected && (
                <TextFeedback
                  rating={rating}
                  textFeedback={textFeedback}
                  setTextFeedback={setTextFeedback}
                />
              )}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            {isFeedbackSubmitted ? (
              rating >= 4 ? null : (
                <Button
                  variant="primary"
                  label="Done"
                  skinny={false}
                  onClick={onClose}
                />
              )
            ) : (
              <Button
                variant="primary"
                label="Submit"
                skinny={false}
                disabled={
                  isSubmitting || !isRatingSelected || !isRatingReasonSelected
                }
                onClick={() => submitFeedback({ feedback })}
              />
            )}
          </Box>
        </Box>
      </ModalContentContainer>
    </Box>
  );
};

const Rating = ({
  rating,
  setRating,
}: {
  rating: number;
  setRating: (rating: number) => void;
}) => (
  <Box>
    <Typography variant="h4" mb={1}>
      How likely are you to recommend this episode?
    </Typography>
    <FeedbackRatings
      rating={rating}
      onRatingChange={setRating}
      ratingScale={5}
    />
  </Box>
);

export const RatingReasons = ({
  rating,
  ratingReasons,
  setRatingReasons,
}: {
  rating: RatingState[0];
  ratingReasons: RatingReasonsState[0];
  setRatingReasons: RatingReasonsState[1];
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Typography variant="h4">
      What are some of the reasons you rated this episode a {rating}?
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 0.5,
      }}>
      {Object.values(ReasonOptions).map(reason => (
        <LabelCheckbox
          key={reason}
          checked={ratingReasons[reason]}
          onCheck={() =>
            setRatingReasons(prev => ({
              ...prev,
              [reason]: !prev[reason],
            }))
          }
          title={reason}
        />
      ))}
    </Box>
    <Typography variant="body3">(Check all that apply)</Typography>
  </Box>
);

export const TextFeedback = ({
  rating,
  textFeedback,
  setTextFeedback,
}: {
  rating: RatingState[0];
  textFeedback: string;
  setTextFeedback: (textFeedback: string) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mb: 2,
      }}>
      <Typography variant="h4">
        {rating > 3
          ? 'What did you enjoy the most about this episode?'
          : 'How could this episode be improved?'}
      </Typography>
      <TextField
        multiline
        minRows={isMobile ? 2 : 3}
        maxRows={isMobile ? 2 : 3}
        placeholder="Your answer"
        aria-label="Feedback"
        value={textFeedback}
        onChange={e => setTextFeedback(e.target.value)}
        sx={[
          appStyles.scrollbar,
          {
            overflowY: 'scroll',
            '& .MuiInputBase-root': {
              borderRadius: 2,
            },
            '& .MuiInputBase-input': {
              ...theme.typography.body2,
            },
          },
        ]}
      />
    </Box>
  );
};
